/**
 * Pointers, pings and ink drawn over the stream.
 *
 * Marks never travel inside the video. Every client draws them on a transparent
 * canvas over its own copy, from fractions (0 to 1) of the captured frame, so
 * they stay sharp at any window size and arrive ahead of the frame they point at.
 * This file is the part with no DOM: geometry, the set of live marks, how they
 * age, and the batching of outgoing points. `MarkLayer.svelte` owns the canvas.
 */

import type { ClientMessage, MarkKind, ServerMessage } from './protocol'

export type MarkPoint = [number, number]

/** The part of a `<video>` element that actually shows the frame, in CSS px. */
export interface FrameRect {
  x: number
  y: number
  w: number
  h: number
}

/** Pings ripple for this long. */
export const PING_MS = 3500
/** A stroke starts fading this long after its last point, and is gone after `INK_FADE_MS`. */
export const INK_HOLD_MS = 8000
export const INK_FADE_MS = 2500
/** A pointer that stops updating (a dropped "hide") disappears on its own. */
export const POINTER_IDLE_MS = 4000
/** Most points kept per stroke, so a very long scribble stays cheap to draw. */
export const MAX_STROKE_POINTS = 600
/** Outgoing pointer and ink points are sent as one message per this interval. */
export const SEND_INTERVAL_MS = 33

/**
 * Where a `object-fit: contain` frame sits inside its element.
 *
 * Returns null until the video reports a size.
 */
export function containRect(
  boxWidth: number,
  boxHeight: number,
  videoWidth: number,
  videoHeight: number,
): FrameRect | null {
  if (!(boxWidth > 0 && boxHeight > 0 && videoWidth > 0 && videoHeight > 0)) return null
  const scale = Math.min(boxWidth / videoWidth, boxHeight / videoHeight)
  const w = videoWidth * scale
  const h = videoHeight * scale
  return { x: (boxWidth - w) / 2, y: (boxHeight - h) / 2, w, h }
}

/** A position inside the element to a fraction of the frame, or null outside it. */
export function toFraction(x: number, y: number, rect: FrameRect): MarkPoint | null {
  if (rect.w <= 0 || rect.h <= 0) return null
  const fx = (x - rect.x) / rect.w
  const fy = (y - rect.y) / rect.h
  if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return null
  return [fx, fy]
}

/** The same colour for the same peer on every screen. */
export function markColor(peerId: string): string {
  let hash = 2166136261
  for (let i = 0; i < peerId.length; i++) {
    hash = Math.imul(hash ^ peerId.charCodeAt(i), 16777619)
  }
  return `hsl(${(hash >>> 0) % 360} 90% 60%)`
}

interface Stroke {
  peer: string
  id: number
  points: MarkPoint[]
  updated: number
}

interface Ping {
  peer: string
  at: MarkPoint
  born: number
}

interface Pointer {
  peer: string
  at: MarkPoint
  updated: number
}

export interface MarkMessage {
  from: string
  kind: MarkKind
  stroke: number
  points: MarkPoint[]
}

/** Everything currently on screen, from anyone including ourselves. */
export class MarkBoard {
  #pointers = new Map<string, Pointer>()
  #strokes = new Map<string, Stroke>()
  #pings: Ping[] = []

  apply(message: MarkMessage, now: number): void {
    const { from, kind, stroke, points } = message
    switch (kind) {
      case 'ping':
        for (const at of points.slice(0, 1)) this.#pings.push({ peer: from, at, born: now })
        break
      case 'pointer': {
        const at = points[points.length - 1]
        if (at) this.#pointers.set(from, { peer: from, at, updated: now })
        else this.#pointers.delete(from)
        break
      }
      case 'ink': {
        if (points.length === 0) break
        const key = `${from}:${stroke}`
        const existing = this.#strokes.get(key)
        const target = existing ?? { peer: from, id: stroke, points: [], updated: now }
        target.points.push(...points)
        if (target.points.length > MAX_STROKE_POINTS) {
          target.points.splice(0, target.points.length - MAX_STROKE_POINTS)
        }
        target.updated = now
        this.#strokes.set(key, target)
        break
      }
    }
  }

  /** Forget everything a peer drew, e.g. when they leave. */
  drop(peer: string): void {
    this.#pointers.delete(peer)
    this.#pings = this.#pings.filter((p) => p.peer !== peer)
    for (const [key, stroke] of this.#strokes) if (stroke.peer === peer) this.#strokes.delete(key)
  }

  clear(): void {
    this.#pointers.clear()
    this.#strokes.clear()
    this.#pings = []
  }

  /** Remove what has faded. Returns whether anything is still live (keep animating). */
  prune(now: number): boolean {
    this.#pings = this.#pings.filter((p) => now - p.born < PING_MS)
    for (const [key, stroke] of this.#strokes) {
      if (now - stroke.updated >= INK_HOLD_MS + INK_FADE_MS) this.#strokes.delete(key)
    }
    for (const [key, pointer] of this.#pointers) {
      if (now - pointer.updated >= POINTER_IDLE_MS) this.#pointers.delete(key)
    }
    return this.live
  }

  get live(): boolean {
    return this.#pings.length > 0 || this.#strokes.size > 0 || this.#pointers.size > 0
  }

  /** Paint onto a canvas whose CSS pixels match the element the frame sits in. */
  draw(
    ctx: CanvasRenderingContext2D,
    rect: FrameRect,
    now: number,
    labelFor?: (peer: string) => string | undefined,
  ): void {
    const at = ([fx, fy]: MarkPoint): MarkPoint => [rect.x + fx * rect.w, rect.y + fy * rect.h]
    const unit = Math.max(1, Math.min(rect.w / 1280, rect.h / 720))
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    for (const stroke of this.#strokes.values()) {
      const idle = now - stroke.updated - INK_HOLD_MS
      const alpha = idle <= 0 ? 1 : Math.max(0, 1 - idle / INK_FADE_MS)
      ctx.globalAlpha = alpha
      ctx.strokeStyle = markColor(stroke.peer)
      ctx.lineWidth = 4 * unit
      ctx.beginPath()
      stroke.points.forEach((point, i) => {
        const [x, y] = at(point)
        // A single tap leaves a dot rather than nothing.
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      if (stroke.points.length === 1) {
        const [x, y] = at(stroke.points[0])
        ctx.lineTo(x + 0.01, y)
      }
      ctx.stroke()
    }

    for (const ping of this.#pings) {
      const age = (now - ping.born) / PING_MS
      const [x, y] = at(ping.at)
      ctx.strokeStyle = markColor(ping.peer)
      ctx.lineWidth = 4 * unit
      for (const lag of [0, 0.25]) {
        const phase = age - lag
        if (phase <= 0 || phase >= 1) continue
        ctx.globalAlpha = 1 - phase
        ctx.beginPath()
        ctx.arc(x, y, (10 + 54 * phase) * unit, 0, Math.PI * 2)
        ctx.stroke()
      }
      ctx.globalAlpha = Math.max(0, 1 - age * 1.5)
      ctx.fillStyle = markColor(ping.peer)
      ctx.beginPath()
      ctx.arc(x, y, 6 * unit, 0, Math.PI * 2)
      ctx.fill()
    }

    for (const pointer of this.#pointers.values()) {
      const [x, y] = at(pointer.at)
      const fade = now - pointer.updated - (POINTER_IDLE_MS - 500)
      ctx.globalAlpha = fade <= 0 ? 1 : Math.max(0, 1 - fade / 500)
      ctx.fillStyle = markColor(pointer.peer)
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)'
      ctx.lineWidth = 2 * unit
      ctx.beginPath()
      ctx.arc(x, y, 8 * unit, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
      const label = labelFor?.(pointer.peer)
      if (label) {
        ctx.font = `600 ${Math.round(13 * unit)}px system-ui, sans-serif`
        ctx.textBaseline = 'middle'
        const width = ctx.measureText(label).width
        const lx = x + 14 * unit
        const ly = y + 4 * unit
        ctx.globalAlpha *= 0.85
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
        ctx.fillRect(lx - 4 * unit, ly - 10 * unit, width + 8 * unit, 20 * unit)
        ctx.globalAlpha = 1
        ctx.fillStyle = '#fff'
        ctx.fillText(label, lx, ly)
      }
    }
    ctx.globalAlpha = 1
  }
}

/** The mark inside a server message, or null for any other message. */
export function markFrom(message: ServerMessage): MarkMessage | null {
  if (message.type !== 'mark') return null
  return {
    from: message.from,
    kind: message.kind,
    stroke: message.stroke,
    points: message.points as MarkPoint[],
  }
}

/**
 * Batches our own pointer and ink points so a fast mouse sends about thirty
 * messages a second, not one per event. Pings go out at once.
 */
export class MarkSender {
  #pending: MarkPoint[] = []
  #pendingKind: 'pointer' | 'ink' | null = null
  #timer: ReturnType<typeof setTimeout> | null = null
  #stroke = 0

  constructor(private readonly send: (message: Extract<ClientMessage, { type: 'mark' }>) => void) {}

  ping(point: MarkPoint): void {
    this.flush()
    this.send({ type: 'mark', kind: 'ping', stroke: 0, points: [point] })
  }

  pointer(point: MarkPoint | null): void {
    if (!point) {
      this.flush()
      this.send({ type: 'mark', kind: 'pointer', stroke: 0, points: [] })
      return
    }
    this.#queue('pointer', point)
  }

  /** Start a new stroke at a point; later `ink` calls extend it. */
  beginStroke(point: MarkPoint): void {
    this.flush()
    this.#stroke = (this.#stroke + 1) >>> 0
    this.#queue('ink', point)
  }

  ink(point: MarkPoint): void {
    this.#queue('ink', point)
  }

  /** Send what is waiting now. Call when a stroke ends. */
  flush(): void {
    if (this.#timer) clearTimeout(this.#timer)
    this.#timer = null
    if (!this.#pendingKind || this.#pending.length === 0) return
    const kind = this.#pendingKind
    // A pointer only needs where it is now; a stroke needs every point.
    const points = kind === 'pointer' ? this.#pending.slice(-1) : this.#pending
    this.#pending = []
    this.#pendingKind = null
    this.send({ type: 'mark', kind, stroke: kind === 'ink' ? this.#stroke : 0, points })
  }

  /** The id of the stroke being drawn, so the local board can use the same one. */
  get stroke(): number {
    return this.#stroke
  }

  #queue(kind: 'pointer' | 'ink', point: MarkPoint): void {
    if (this.#pendingKind && this.#pendingKind !== kind) this.flush()
    this.#pendingKind = kind
    this.#pending.push(point)
    // The server keeps at most 32 points per message; send before reaching that.
    if (this.#pending.length >= 30) {
      this.flush()
      return
    }
    this.#timer ??= setTimeout(() => this.flush(), SEND_INTERVAL_MS)
  }
}
